/**
 * MenuListPane — lewa kolumna edytora menu tenanta (makieta Eugenii 2026-07-02,
 * wzór: lista „MENU MODUŁU" w edytorze szablonu huba):
 *
 *  MENU SYSTEMOWE          — gotowe menu z wykupionych modułów, read-only;
 *    <wiersz>  [Duplikuj]    klik wiersza = modal z podglądem menu,
 *                            Duplikuj = kopia staje się menu customowym.
 *  MENU CUSTOMOWE   [+]    — własne menu klienta; klik wiersza = edytor po prawej;
 *    [ ] Aktywne [ ] Nieaktywne   filtr listy (checkboxy);
 *    <ikona> <nazwa> [chip]  [⧉][✎][🗑]   akcje: duplikuj / edytuj (modal: nazwa, ikona, kolor,
 *                            aktywność) / usuń. Ikona w kolorze menu, chip Aktywne / Nieaktywne.
 *
 * Menu systemowych nie można edytować — tylko podgląd i duplikat (nowe menu).
 * Puste własne układy (isEmpty, np. seedowany „Default") nie są listowane.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuLayoutSummary } from '@saas/shared';
import {
  Copy,
  Download,
  MenuSquare,
  PanelLeftClose,
  Pencil,
  Plus,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Checkbox } from '@/components/Checkbox';
import { IconButton } from '@/components/IconButton';
import { resolvePickerIcon } from '@/components/modules/IconPicker';
import { resolveIcon } from '@/lib/icon-map';
import { resolveLucideIcon } from '@/lib/lucide-all-icons';
import { menuColorCss, parseMenuColor } from '@/lib/menu-colors';

import type { GalleryModuleMenu } from './MenuLayoutGallery';

/** Ikona wiersza menu customowego: pełny zestaw lucide → domyślna. */
function CustomRowIcon({ layout }: { layout: MenuLayoutSummary }) {
  const Icon = resolveLucideIcon(layout.icon) ?? MenuSquare;
  const color = parseMenuColor(layout.color);
  return (
    <Icon
      className="h-4 w-4 flex-shrink-0"
      style={color ? { color: menuColorCss(color) } : undefined}
      aria-hidden="true"
      data-testid={`menu-list-custom-icon-${layout.id}`}
    />
  );
}

export interface MenuListPaneProps {
  /** Menu systemowe (z wykupionych modułów) — read-only. */
  moduleMenus: GalleryModuleMenu[];
  /** Własne menu klienta (bez pustych — filtrowane w shellu). */
  layouts: MenuLayoutSummary[];
  isLoading: boolean;
  /** Id menu otwartego w edytorze (podświetlenie wiersza). */
  openLayoutId: string | null;
  onSystemPreview: (code: string) => void;
  onSystemDuplicate: (code: string) => void;
  onAddCustom: () => void;
  onOpenCustom: (id: string) => void;
  onDuplicateCustom: (id: string) => void;
  /** Ołówek na wierszu — modal ustawień menu (nazwa, ikona, kolor, aktywność). */
  onEditCustom: (id: string) => void;
  onDeleteCustom: (id: string) => void;
  /** Eksport menu do pliku (przeniesienie do innego tenanta). Brak = bez przycisku (panel ITE). */
  onExportCustom?: (id: string) => void;
  /** Import menu z pliku. Brak = bez przycisku (panel ITE, brak prawa edycji menu). */
  onImportCustom?: () => void;
  i18nPrefix: string;
  /** Szerokość kolumny w px (ręczne poszerzanie — uchwyt w shellu). Undefined = domyślna. */
  widthPx?: number;
  /** Zwinięcie kolumny — ikonka w prawym górnym rogu. Brak = bez ikonki. */
  onCollapse?: () => void;
}

export function MenuListPane({
  moduleMenus,
  layouts,
  isLoading,
  openLayoutId,
  onSystemPreview,
  onSystemDuplicate,
  onAddCustom,
  onOpenCustom,
  onDuplicateCustom,
  onEditCustom,
  onDeleteCustom,
  onExportCustom,
  onImportCustom,
  i18nPrefix,
  widthPx,
  onCollapse,
}: MenuListPaneProps) {
  const { t } = useTranslation();

  // Filtr listy menu customowych: oba zaznaczone = wszystkie (stan tylko lokalny, per sesja).
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const visibleLayouts = layouts.filter((l) => {
    const active = l.isActive !== false;
    return active ? showActive : showInactive;
  });

  // Promienie wg systemu (jak reszta apki): wiersze-karty rounded-xl (12), przyciski tekstowe
  // pill (parytet z globalnym Button), ikony rounded-lg.
  const rowClass = (active: boolean) =>
    [
      'group flex items-center gap-2 rounded-xl border px-2 py-1.5',
      active
        ? 'border-accent-400 bg-accent-50 dark:bg-accent-950/30'
        : 'border-border-secondary bg-bg-elevated hover:border-accent-300',
    ].join(' ');

  const textBtnClass =
    'flex shrink-0 items-center gap-1 rounded-full border border-border-secondary px-2.5 py-1 text-xs text-text-secondary transition hover:border-accent-300 hover:text-text-primary';
  const iconBtnClass =
    'flex items-center rounded-lg p-1 text-text-tertiary transition hover:bg-bg-tertiary hover:text-text-primary';

  return (
    <aside
      className="flex w-72 flex-none shrink-0 flex-col gap-5 overflow-y-auto border-r border-border-secondary bg-bg-secondary p-3"
      style={widthPx ? { width: `${widthPx}px` } : undefined}
      data-testid="menu-list-pane"
    >
      {onCollapse ? (
        <div className="-mb-3 flex justify-end">
          <button
            type="button"
            onClick={onCollapse}
            aria-label={t(`${i18nPrefix}.hideMenuList`, { defaultValue: 'Ukryj listę menu' })}
            title={t(`${i18nPrefix}.hideMenuList`, { defaultValue: 'Ukryj listę menu' })}
            className="rounded-md p-1 text-text-muted transition hover:bg-bg-elevated hover:text-text-primary"
            data-testid="menu-list-collapse"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>
      ) : null}
      {/* Menu systemowe — read-only, klik = podgląd, Duplikuj = kopia do customowych */}
      <section data-testid="menu-list-system">
        <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
          {t(`${i18nPrefix}.listPane.systemHeader`, { defaultValue: 'Menu systemowe' })}
        </h2>
        {moduleMenus.length === 0 ? (
          <p className="text-xs text-text-muted">
            {isLoading
              ? t(`${i18nPrefix}.palette.loading`)
              : t(`${i18nPrefix}.listPane.systemEmpty`, {
                  defaultValue: 'Brak menu z wykupionych modułów.',
                })}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {moduleMenus.map((m) => {
              const Icon = resolvePickerIcon(m.icon) ?? resolveIcon(m.icon);
              return (
                <li key={m.code} className={rowClass(false)}>
                  <button
                    type="button"
                    onClick={() => onSystemPreview(m.code)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    title={t(`${i18nPrefix}.gallery.previewModuleMenu`, {
                      defaultValue: 'Podgląd',
                    })}
                    data-testid={`menu-list-system-open-${m.code}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0 text-text-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-text-primary">
                        {m.name}
                      </span>
                      <span className="block text-xs text-text-muted">
                        {t(`${i18nPrefix}.gallery.positionsCount`, {
                          count: m.positions,
                          defaultValue: 'pozycje: {{count}}',
                        })}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onSystemDuplicate(m.code)}
                    className={textBtnClass}
                    data-testid={`menu-list-system-duplicate-${m.code}`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {t(`${i18nPrefix}.layouts.actions.duplicate`)}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Menu customowe — własne menu klienta, „+" tworzy nowe */}
      <section data-testid="menu-list-custom">
        <div className="mb-1.5 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            {t(`${i18nPrefix}.listPane.customHeader`, { defaultValue: 'Menu customowe' })}
          </h2>
          <span className="flex items-center gap-1">
            {onImportCustom ? (
              <IconButton
                size="sm"
                variant="ghost"
                onClick={onImportCustom}
                aria-label={t(`${i18nPrefix}.import.open`)}
                title={t(`${i18nPrefix}.import.open`)}
                data-testid="menu-list-custom-import"
              >
                <Upload />
              </IconButton>
            ) : null}
            <button
              type="button"
              onClick={onAddCustom}
              className="flex items-center rounded-md border border-border-secondary p-1 text-text-secondary transition hover:border-accent-300 hover:text-text-primary"
              aria-label={t(`${i18nPrefix}.layouts.actions.new`)}
              title={t(`${i18nPrefix}.layouts.actions.new`)}
              data-testid="menu-list-custom-add"
            >
              <Plus className="h-4 w-4" />
            </button>
          </span>
        </div>
        {layouts.length > 0 ? (
          <div
            className="mb-2 flex items-center gap-4"
            role="group"
            aria-label={t(`${i18nPrefix}.listPane.filterLabel`)}
            data-testid="menu-list-custom-filter"
          >
            <Checkbox
              checked={showActive}
              onChange={(e) => setShowActive(e.target.checked)}
              label={t(`${i18nPrefix}.layouts.status.active`)}
              data-testid="menu-list-filter-active"
            />
            <Checkbox
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              label={t(`${i18nPrefix}.layouts.status.inactive`)}
              data-testid="menu-list-filter-inactive"
            />
          </div>
        ) : null}
        {layouts.length === 0 ? (
          <p className="text-xs text-text-muted" data-testid="menu-list-custom-empty">
            {isLoading
              ? t(`${i18nPrefix}.palette.loading`)
              : t(`${i18nPrefix}.listPane.customEmpty`, {
                  defaultValue: 'Brak własnych menu — utwórz pierwsze plusem.',
                })}
          </p>
        ) : visibleLayouts.length === 0 ? (
          <p className="text-xs text-text-muted" data-testid="menu-list-custom-filtered-empty">
            {t(`${i18nPrefix}.listPane.filteredEmpty`)}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {visibleLayouts.map((l) => {
              const active = l.isActive !== false;
              return (
                <li
                  key={l.id}
                  className={[rowClass(l.id === openLayoutId), active ? '' : 'opacity-75'].join(
                    ' ',
                  )}
                  data-testid={`menu-list-custom-row-${l.id}`}
                  data-active={active}
                >
                  <CustomRowIcon layout={l} />
                  <button
                    type="button"
                    onClick={() => onOpenCustom(l.id)}
                    className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-left"
                    data-testid={`menu-list-custom-open-${l.id}`}
                  >
                    <span className="truncate text-sm font-medium text-text-primary">{l.name}</span>
                    {l.isDefault ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                        <Star className="h-3 w-3" />
                        {t(`${i18nPrefix}.layouts.isDefaultBadge`)}
                      </span>
                    ) : null}
                    <Badge
                      size="sm"
                      tone={active ? 'success' : 'neutral'}
                      data-testid={`menu-list-custom-status-${l.id}`}
                    >
                      {active
                        ? t(`${i18nPrefix}.layouts.status.active`)
                        : t(`${i18nPrefix}.layouts.status.inactive`)}
                    </Badge>
                  </button>
                  <span className="flex shrink-0 items-center gap-0.5">
                    {onExportCustom ? (
                      <IconButton
                        size="sm"
                        variant="ghost"
                        onClick={() => onExportCustom(l.id)}
                        aria-label={t(`${i18nPrefix}.export.action`)}
                        title={t(`${i18nPrefix}.export.action`)}
                        data-testid={`menu-list-custom-export-${l.id}`}
                      >
                        <Download />
                      </IconButton>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onDuplicateCustom(l.id)}
                      className={iconBtnClass}
                      aria-label={t(`${i18nPrefix}.layouts.actions.duplicate`)}
                      title={t(`${i18nPrefix}.layouts.actions.duplicate`)}
                      data-testid={`menu-list-custom-duplicate-${l.id}`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditCustom(l.id)}
                      className={iconBtnClass}
                      aria-label={t(`${i18nPrefix}.layouts.actions.edit`)}
                      title={t(`${i18nPrefix}.layouts.actions.edit`)}
                      data-testid={`menu-list-custom-rename-${l.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteCustom(l.id)}
                      className="flex items-center rounded p-1 text-red-500 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      aria-label={t(`${i18nPrefix}.layouts.actions.delete`)}
                      title={t(`${i18nPrefix}.layouts.actions.delete`)}
                      data-testid={`menu-list-custom-delete-${l.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </aside>
  );
}
